import type { ReactNode } from 'react'

import { Toaster } from 'react-hot-toast'

import { AuthProvider, useAuth } from 'src/auth'
import { AgentChatProvider } from 'src/components/AnalyticsChat/AgentChatProvider'
import FatalErrorPage from 'src/pages/FatalErrorPage/FatalErrorPage'
import { GraphQLProvider } from 'src/platform/apollo'
import { FatalErrorBoundary } from 'src/platform/errorBoundary'
import { HeadProvider } from 'src/platform/meta'

import { ClientEventProvider } from './contexts/client-event-context'
import './scaffold.css'
import './index.css'
import { milano } from './utils/milano'

// Intercom
if (process.env.TWODOTS_ENV === 'prod') {
  ;(function () {
    const w = window
    const ic = w.Intercom
    if (typeof ic === 'function') {
      ic('reattach_activator')
      ic('update', w.intercomSettings)
    } else {
      const d = document
      const i = function (...args: unknown[]) {
        i.c(args)
      } as {
        (...args: unknown[]): void
        q: unknown[][]
        c: (args: unknown[]) => void
      }
      i.q = [] as unknown[][]
      i.c = function (args: unknown[]) {
        i.q.push(args)
      }
      w.Intercom = i
      const l = function () {
        const s = d.createElement('script')
        s.type = 'text/javascript'
        s.async = true
        s.src = 'https://widget.intercom.io/widget/dqkyw6ws'
        const x = d.getElementsByTagName('script')[0]
        x.parentNode?.insertBefore(s, x)
      }
      if (document.readyState === 'complete') {
        l()
      } else if (w.attachEvent) {
        w.attachEvent('onload', l)
      } else {
        w.addEventListener('load', l, false)
      }
    }
  })()
} else {
  ;(function () {
    const w = window
    const ic = w.Intercom
    if (typeof ic === 'function') {
      ic('reattach_activator')
      ic('update', w.intercomSettings)
    } else {
      const d = document
      const i = function (...args: unknown[]) {
        i.c(args)
      } as {
        (...args: unknown[]): void
        q: unknown[][]
        c: (args: unknown[]) => void
      }
      i.q = [] as unknown[][]
      i.c = function (args: unknown[]) {
        i.q.push(args)
      }
      w.Intercom = i
      const l = function () {
        const s = d.createElement('script')
        s.type = 'text/javascript'
        s.async = true
        s.src = 'https://widget.intercom.io/widget/ca9ahjig'
        const x = d.getElementsByTagName('script')[0]
        x.parentNode?.insertBefore(s, x)
      }
      if (document.readyState === 'complete') {
        l()
      } else if (w.attachEvent) {
        w.attachEvent('onload', l)
      } else {
        w.addEventListener('load', l, false)
      }
    }
  })()
}

interface AppProps {
  children?: ReactNode
}

const App = ({ children }: AppProps) => {
  return (
    <FatalErrorBoundary page={FatalErrorPage}>
      <HeadProvider titleTemplate="%PageTitle | %AppTitle">
        <AuthProvider>
          <ClientEventProvider>
            <GraphQLProvider
              useAuth={useAuth}
              headers={{ 'x-milano-id': milano() }}
              cacheConfig={{
                typePolicies: {
                  // Don't normalize: these timeline rows mix two id spaces
                  // (assignment ids and pricing-version ids), so default
                  // (__typename + id) keying would collapse a colliding pair.
                  PropertyAssignmentHistoryEntry: { keyFields: false },
                },
              }}
            >
              <Toaster position="top-center" />
              <AgentChatProvider>{children}</AgentChatProvider>
            </GraphQLProvider>
          </ClientEventProvider>
        </AuthProvider>
      </HeadProvider>
    </FatalErrorBoundary>
  )
}

export default App
